import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import type { CountryCode } from 'libphonenumber-js/mobile';
import { Art } from '../components/Art';
import { Button, Sheet, TopBar } from '../components/ui';
import { Icon } from '../components/Icon';
import { Screen } from '../components/Screen';
import { Stage } from '../components/Stage';
import { strip_phone } from '../art/art';
import { VehicleArt } from '../art/Vehicle';
import { AuthHero } from '../components/AuthHero';
import { useLayout } from '../lib/layout';
import { ApiError, type AuthConfig } from '../lib/api';
import { useApp } from '../lib/app';
import { friendly } from '../lib/errors';
import { useT } from '../lib/i18n';
import { countries, countryOf, e164, formatNational, fromPaste, isCountry, isEmail, maxDigits, rememberCountry, searchCountries, startCountry } from '../lib/phone';
import { Keypad } from './Keypad';

export const SIGNIN_KEY = 'zr-signin';
/** The sign-in in progress: who the code went to (an E.164 phone or an email), for the code screen. */
export type SigninState = { phone?: string; email?: string; national?: string; country?: CountryCode; devCode?: string; sentAt: number; codeLength: number };
type Method = 'phone' | 'email';

const readState = (): SigninState | null => {
  try {
    return JSON.parse(sessionStorage.getItem(SIGNIN_KEY) ?? 'null') as SigninState | null;
  } catch {
    return null;
  }
};

/** The countries the server signs in (`countries`: "all" or a list); every country until it says otherwise. */
const allowed = (cfg: AuthConfig | null) => (cfg && Array.isArray(cfg.countries) ? new Set(cfg.countries) : null);

/** Country picker: a searchable list of every country, flag + name + code. */
export function CountrySheet({ open, onClose, value, onPick, only }: { open: boolean; onClose: () => void; value: CountryCode; onPick: (c: CountryCode) => void; only: Set<string> | null }) {
  const t = useT();
  const [q, setQ] = useState('');
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (open) {
      setQ('');
      window.setTimeout(() => input.current?.focus(), 60);
    }
  }, [open]);
  const list = useMemo(() => searchCountries(q, countries().filter((c) => !only || only.has(c.code))), [q, only]);
  return (
    <Sheet open={open} onClose={onClose} title={t('phone.country')}>
      <input
        ref={input}
        type="search"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onKeyDown={(e) => e.key === 'Enter' && list[0] && (e.preventDefault(), onPick(list[0].code))}
        placeholder={t('phone.countrySearch')}
        aria-label={t('phone.countrySearch')}
        autoComplete="off"
        className="zr-field"
        style={{ marginTop: 14 }}
      />
      <div role="listbox" aria-label={t('phone.country')} style={{ marginTop: 8, display: 'flex', flexDirection: 'column' }}>
        {list.length === 0 && <p style={{ margin: '14px 4px', fontSize: 14, color: 'var(--ink2)' }}>{t('phone.countryNone')}</p>}
        {list.map((c) => (
          <button
            key={c.code}
            type="button"
            role="option"
            aria-selected={c.code === value}
            onClick={() => onPick(c.code)}
            className="zr-row zr-press-soft"
            style={{ minHeight: 52, padding: '0 10px', borderRadius: 14, background: c.code === value ? 'var(--acSoft)' : undefined }}
          >
            <span aria-hidden="true" style={{ fontSize: 22, lineHeight: '24px', width: 30, textAlign: 'center' }}>{c.flag}</span>
            <span className="zr-ellipsis" style={{ flex: 1, minWidth: 0, fontSize: 15, fontWeight: 700 }}>{c.name}</span>
            <span className="zr-num" style={{ fontSize: 14, fontWeight: 500, color: 'var(--ink2)' }}>{c.dial}</span>
            {c.code === value && <Icon name="check" size={18} sw={2.6} style={{ color: 'var(--acText)' }} />}
          </button>
        ))}
      </div>
    </Sheet>
  );
}

export function Phone() {
  const { api } = useApp();
  const t = useT();
  const nav = useNavigate();
  const saved = readState();
  const [cfg, setCfg] = useState<AuthConfig | null>(null);
  const [method, setMethod] = useState<Method>(saved?.email ? 'email' : 'phone');
  const [country, setCountry] = useState<CountryCode>(() => (saved?.country && isCountry(saved.country) ? saved.country : startCountry()));
  const [digits, setDigits] = useState(saved?.national ?? '');
  const [email, setEmail] = useState(saved?.email ?? '');
  const [picking, setPicking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const L = useLayout();
  const emailInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    api.authConfig().then(
      (c) => {
        setCfg(c);
        // the server's default country, unless the rider already chose one
        if (!saved?.country) setCountry(startCountry(c.defaultCountry));
      },
      () => undefined,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api]);
  // only the ways the server offers (older servers: phone only)
  const methods: Method[] = cfg?.methods?.length ? cfg.methods.filter((m): m is Method => m === 'phone' || m === 'email') : ['phone'];
  const mode: Method = methods.includes(method) ? method : methods[0];
  const only = allowed(cfg);
  const codeLength = (mode === 'email' ? cfg?.emailCodeLength : cfg?.codeLength) ?? 6;
  const c = countryOf(country);

  const add = (d: string) => {
    setError(null);
    setDigits((x) => (x + d).replace(/\D/g, '').slice(0, maxDigits(country)));
  };
  const back = () => setDigits((x) => x.slice(0, -1));

  // a hardware keyboard types the number straight in (the drawn pad is for touch)
  useEffect(() => {
    if (mode !== 'phone' || picking) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement && e.target.id !== 'mobile') return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (/^\d$/.test(e.key)) add(e.key);
      else if (e.key === 'Backspace') back();
      else if (e.key === 'Enter') void send();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const phone = e164(digits, country);
  const valid = mode === 'phone' ? !!phone : isEmail(email);
  async function send() {
    if (!valid || busy) {
      if (!valid) setError(mode === 'phone' ? t('phone.invalid', { country: c.name }) : t('email.invalid'));
      return;
    }
    setBusy(true);
    setError(null);
    const to = mode === 'phone' ? { phone: phone! } : { email: email.trim().toLowerCase() };
    if (mode === 'phone') rememberCountry(country);
    const st = (r?: { devCode?: string; codeLength?: number }): SigninState => ({
      ...(mode === 'phone' ? { phone: phone!, national: digits, country } : { email: email.trim().toLowerCase() }),
      devCode: import.meta.env.DEV ? r?.devCode : undefined,
      sentAt: Date.now(),
      codeLength: r?.codeLength ?? codeLength,
    });
    try {
      const r = await api.requestOtp(to);
      sessionStorage.setItem(SIGNIN_KEY, JSON.stringify(st(r)));
      nav({ to: '/signin/code' });
    } catch (e) {
      if (e instanceof ApiError && e.code === 'OTP_COOLDOWN') {
        // a code was sent a moment ago: go and type it
        sessionStorage.setItem(SIGNIN_KEY, JSON.stringify(st()));
        nav({ to: '/signin/code' });
        return;
      }
      setError(friendly(e, t));
    } finally {
      setBusy(false);
    }
  }

  const switchTo = (m: Method) => {
    setMethod(m);
    setError(null);
    if (m === 'email') window.setTimeout(() => emailInput.current?.focus(), 30);
  };

  const header = <TopBar onBack={() => nav({ to: '/' })} right={<span style={{ fontSize: 13, lineHeight: '16px', fontWeight: 700, color: 'var(--ink3)' }}>{t('phone.step')}</span>} style={L.wide ? { padding: 0 } : undefined} />;
  const fieldBox = { marginTop: 8, height: 64, borderRadius: 18, background: 'var(--s1)', boxShadow: `inset 0 0 0 1.5px ${error ? 'var(--danger)' : 'var(--focus)'}`, display: 'flex', alignItems: 'center', gap: 12, padding: '0 18px 0 8px', boxSizing: 'border-box' as const };
  const form = (
    <div style={{ flex: 'none', padding: L.wide ? 0 : '0 24px', marginTop: 'calc(24px * var(--vg))' }}>
      <h1 style={{ margin: 0, fontSize: L.wide ? 40 : 32, lineHeight: L.wide ? '44px' : '36px', fontWeight: 900, letterSpacing: '-0.03em' }}>{mode === 'phone' ? t('phone.title') : t('email.title')}</h1>
      <p style={{ margin: '10px 0 0', fontSize: 16, lineHeight: '24px', fontWeight: 500, color: 'var(--ink2)' }}>{mode === 'phone' ? t('phone.lead', { n: codeLength }) : t('email.lead', { n: codeLength })}</p>

      {methods.length > 1 && (
        // Phone / Email, only when the server signs in both ways
        <div role="radiogroup" aria-label={t('signin.method')} className="zr-seg2" style={{ marginTop: 'calc(22px * var(--vg))' }}>
          {methods.map((m) => (
            <button key={m} type="button" role="radio" aria-checked={mode === m} onClick={() => switchTo(m)}>
              <Icon name={m === 'phone' ? 'phone' : 'mail'} size={17} />
              {m === 'phone' ? t('signin.phone') : t('signin.email')}
            </button>
          ))}
        </div>
      )}

      {mode === 'phone' ? (
        <>
          <label htmlFor="mobile" style={{ display: 'block', marginTop: methods.length > 1 ? 18 : 'calc(32px * var(--vg))', fontSize: 13, lineHeight: '16px', fontWeight: 700, color: 'var(--ink2)' }}>
            {t('phone.label')}
          </label>
          <div style={fieldBox}>
            <button type="button" onClick={() => setPicking(true)} aria-label={t('phone.countryAria', { name: c.name, dial: c.dial })} aria-haspopup="dialog" className="zr-press-soft" style={{ flex: 'none', height: 48, padding: '0 8px 0 10px', border: 0, borderRadius: 14, background: 'transparent', color: 'var(--ink)', display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
              <span aria-hidden="true" style={{ fontSize: 22, lineHeight: '24px' }}>{c.flag}</span>
              <span className="zr-num" style={{ fontSize: 20, fontWeight: 700 }}>{c.dial}</span>
              <Icon name="chevronDown" size={16} sw={2.2} style={{ color: 'var(--ink2)' }} />
            </button>
            <span aria-hidden="true" style={{ width: 1, height: 28, background: 'var(--line2)' }} />
            <input
              id="mobile"
              type="tel"
              inputMode="none"
              autoComplete="tel-national"
              autoFocus={L.wide}
              value={formatNational(digits, country)}
              onChange={(e) => setDigits(e.target.value.replace(/\D/g, '').slice(0, maxDigits(country)))}
              onKeyDown={(e) => e.preventDefault()}
              onPaste={(e) => {
                e.preventDefault();
                const p = fromPaste(e.clipboardData.getData('text'), country);
                if (p.country !== country && (!only || only.has(p.country))) setCountry(p.country);
                setDigits(p.national);
              }}
              aria-invalid={!!error}
              aria-describedby="mobile-help"
              style={{ flex: 1, minWidth: 0, height: 64, border: 0, outline: 0, padding: 0, background: 'transparent', color: 'var(--ink)', fontSize: 22, fontWeight: 700, letterSpacing: '0.02em', fontVariantNumeric: 'tabular-nums', caretColor: 'var(--focus)' }}
            />
          </div>
        </>
      ) : (
        <>
          <label htmlFor="email" style={{ display: 'block', marginTop: 18, fontSize: 13, lineHeight: '16px', fontWeight: 700, color: 'var(--ink2)' }}>
            {t('email.label')}
          </label>
          <div style={{ ...fieldBox, padding: '0 18px' }}>
            <input
              id="email"
              ref={emailInput}
              type="email"
              inputMode="email"
              autoComplete="email"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              enterKeyHint="send"
              value={email}
              onChange={(e) => (setError(null), setEmail(e.target.value))}
              onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), void send())}
              placeholder="name@example.com"
              aria-invalid={!!error}
              aria-describedby="mobile-help"
              style={{ flex: 1, minWidth: 0, height: 64, border: 0, outline: 0, padding: 0, background: 'transparent', color: 'var(--ink)', fontSize: 19, fontWeight: 700 }}
            />
          </div>
        </>
      )}
      <p id="mobile-help" role={error ? 'alert' : undefined} key={error ?? 'help'} className={error ? 'zr-tick' : undefined} style={{ margin: '10px 0 0', display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, lineHeight: '18px', fontWeight: 500, color: error ? 'var(--danger)' : 'var(--ink3)' }}>
        <Icon name={error ? 'alert' : 'lock'} size={16} />
        {error ?? (mode === 'phone' ? t('phone.private') : t('email.private'))}
      </p>
    </div>
  );
  const cta = (
    <div className={L.wide ? undefined : 'zr-gutter'} style={{ flex: 'none', paddingBottom: 10, paddingTop: L.wide ? 24 : 0, position: 'relative', zIndex: 6 }}>
      <Button onClick={() => void send()} busy={busy} soft={!valid}>
        {t('phone.send')}
      </Button>
    </div>
  );
  const picker = (
    <CountrySheet
      open={picking}
      onClose={() => setPicking(false)}
      value={country}
      only={only}
      onPick={(code) => {
        setCountry(code);
        setDigits((d) => d.slice(0, maxDigits(code)));
        setError(null);
        setPicking(false);
      }}
    />
  );
  // the drawn number pad is for the phone number only (an email uses the phone's own keyboard)
  const pad = mode === 'phone' ? <Keypad onDigit={add} onBack={back} /> : null;

  if (L.wide)
    return (
      <AuthHero name="phone-number" label="Phone number">
        {header}
        {form}
        {cta}
        {pad}
        {picker}
      </AuthHero>
    );

  return (
    <Screen name="phone-number" label="Phone number">
      <div className="zr-col zr-top">
        {header}
        {form}
        <div className="zr-fill" style={{ position: 'relative', overflow: 'hidden', minHeight: 12 }}>
          <Stage bottom={2} height={80}>
            <Art piece={strip_phone} />
            <VehicleArt type="AUTO" width={44} height={26} shadow={false} style={{ position: 'absolute', left: 240, top: 44 }} />
          </Stage>
        </div>
        {cta}
        {pad}
      </div>
      {picker}
    </Screen>
  );
}
